export interface PcmCapture { stop: () => void }
export function pcmBase64(chunks: Int16Array[]): string {
  const length = chunks.reduce((n, chunk) => n + chunk.length, 0);
  const bytes = new Uint8Array(length * 2), view = new DataView(bytes.buffer);
  let offset = 0;
  for (const chunk of chunks) for (const sample of chunk) { view.setInt16(offset, sample, true); offset += 2; }
  let binary = '';
  for (let start = 0; start < bytes.length; start += 8192) binary += String.fromCharCode(...bytes.subarray(start, start + 8192));
  return btoa(binary);
}

/** Local voice activity detection; audio leaves the device only after speech followed by silence. */
export async function startPcmCapture(signal: AbortSignal, onSpeech: () => void, onUtterance: (base64: string) => void, onError: (message: string) => void): Promise<PcmCapture> {
  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia || !window.AudioContext)
    throw new Error('Use a secure browser tab with microphone support. Embedded views may block microphone access.');
  let stream: MediaStream | undefined, context: AudioContext | undefined, source: MediaStreamAudioSourceNode | undefined, processor: AudioWorkletNode | undefined;
  let stopped = false;
  const stop = () => {
    if (stopped) return;
    stopped = true;
    signal.removeEventListener('abort', stop);
    if (processor) { processor.port.onmessage = null; processor.disconnect(); }
    source?.disconnect();
    stream?.getTracks().forEach(track => track.stop());
    void context?.close().catch(() => undefined);
  };
  signal.addEventListener('abort', stop, { once: true });
  try {
    stream = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true } });
    if (stopped || signal.aborted) { stream.getTracks().forEach(track => track.stop()); throw new DOMException('Cancelled', 'AbortError'); }
    context = new AudioContext({ sampleRate: 16000 });
    if (context.sampleRate !== 16000) throw new Error('This browser cannot capture 16kHz voice. Continue with dictation or typing.');
    await context.audioWorklet.addModule('/voice-capture-worklet.js');
    if (stopped || signal.aborted) throw new DOMException('Cancelled', 'AbortError');
    source = context.createMediaStreamSource(stream);
    processor = new AudioWorkletNode(context, 'voice-capture');
    let chunks: Int16Array[] = [], preRoll: Int16Array[] = [], samples = 0, quiet = 0, voiced = 0, announced = false;
    processor.port.onmessage = (event: MessageEvent<unknown>) => {
      if (stopped || signal.aborted || !(event.data instanceof ArrayBuffer)) return;
      const chunk = new Int16Array(event.data);
      const rms = Math.sqrt(chunk.reduce((sum, value) => sum + value * value, 0) / Math.max(1, chunk.length)) / 32768;
      if (rms > 0.025) {
        if (!chunks.length) { chunks = preRoll; samples = preRoll.reduce((n, part) => n + part.length, 0); preRoll = []; }
        quiet = 0; voiced += chunk.length;
        if (voiced >= 2560 && !announced) { announced = true; onSpeech(); }
      } else quiet += chunk.length;
      if (chunks.length || rms > 0.025) {
        chunks.push(chunk); samples += chunk.length;
        if (samples > 448000) { onError('Keep each spoken message under 28 seconds. Voice has stopped; continue in text.'); stop(); return; }
        if (quiet > 11200) {
          if (announced) onUtterance(pcmBase64(chunks));
          chunks = []; samples = 0; voiced = 0; quiet = 0; announced = false;
        }
      } else {
        preRoll.push(chunk);
        if (preRoll.length > 25) preRoll.shift();
        voiced = 0;
      }
    };
    stream.getTracks().forEach(track => { track.onended = () => { if (!stopped) { onError('Microphone disconnected. Start voice again.'); stop(); } }; });
    source.connect(processor); processor.connect(context.destination);
    await context.resume();
    return { stop };
  } catch (error) { stop(); throw error; }
}
